/**
 * Features Service - API Client for High-Priority Features
 * 
 * Centralized API client for all 5 high-priority features
 * Uses httpClient for consistent auth/error handling
 */

import http from './httpClient';

class FeaturesService {
    // ============================================================================
    // HEALTH & STATUS
    // ============================================================================

    static async checkHealth() {
        return http.get('/features/health');
    }

    // ============================================================================
    // 1. VIRTUAL INVENTORY DEDUCTION (V.I.D.)
    // ============================================================================

    static async createVirtualItem(data) {
        return http.post('/features/virtual-items', data);
    }

    static async getVirtualItems() {
        return http.get('/features/virtual-items');
    }

    static async updateYieldFactor(itemId, yieldFactor) {
        return http.put(`/features/virtual-items/${itemId}/yield`, { yieldFactor });
    }

    static async convertPhysicalToVirtual(data) {
        return http.post('/features/virtual-items/convert', data);
    }

    static async getWastageReport(startDate, endDate) {
        return http.get(`/features/wastage/report?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`);
    }

    static async recordWastage(data) {
        return http.post('/features/wastage/record', data);
    }

    // ============================================================================
    // 2. COMPLIMENTARY ITEM MAPPING
    // ============================================================================

    static async createComplimentaryRecipe(data) {
        return http.post('/features/complimentary-recipes', data);
    }

    static async getComplimentaryRecipes(activeOnly = true) {
        return http.get(`/features/complimentary-recipes?activeOnly=${activeOnly}`);
    }

    static async getComplimentaryRecipeById(id) {
        return http.get(`/features/complimentary-recipes/${id}`);
    }

    static async updateComplimentaryRecipe(id, data) {
        return http.put(`/features/complimentary-recipes/${id}`, data);
    }

    static async deleteComplimentaryRecipe(id) {
        return http.delete(`/features/complimentary-recipes/${id}`);
    }

    static async linkComplimentaryToRecipe(recipeId, data) {
        return http.post(`/features/recipes/${recipeId}/complimentary/link`, data);
    }

    static async getLinkedComplimentaryItems(recipeId) {
        return http.get(`/features/recipes/${recipeId}/complimentary`);
    }

    static async unlinkComplimentaryItem(linkId) {
        return http.delete(`/features/complimentary/links/${linkId}`);
    }

    static async calculateTotalDeductions(recipeId, portionMultiplier = 1.0) {
        return http.get(`/features/recipes/${recipeId}/total-deductions?portionMultiplier=${portionMultiplier}`);
    }

    // ============================================================================
    // 3. PARENT RECIPE SCALING (VARIANTS)
    // ============================================================================

    static async createParentRecipeWithVariants(data) {
        return http.post('/features/recipes/parent-with-variants', data);
    }

    static async addVariantToParentRecipe(parentRecipeId, data) {
        return http.post(`/features/recipes/${parentRecipeId}/variants`, data);
    }

    static async getRecipeVariants(parentRecipeId) {
        return http.get(`/features/recipes/${parentRecipeId}/variants`);
    }

    static async getVariantIngredients(variantMenuItemId) {
        return http.get(`/features/recipes/variant/${variantMenuItemId}/ingredients`);
    }

    static async updateParentRecipeIngredients(parentRecipeId, ingredients) {
        return http.put(`/features/recipes/${parentRecipeId}/ingredients`, { ingredients });
    }

    static async updateVariantMultiplier(variantId, multiplier) {
        return http.put(`/features/variants/${variantId}/multiplier`, { multiplier });
    }

    static async convertToParentVariant(menuItemId, data) {
        return http.post(`/features/recipes/${menuItemId}/convert-to-variant`, data);
    }

    static async getParentRecipeForVariant(variantMenuItemId) {
        return http.get(`/features/recipes/variant/${variantMenuItemId}/parent`);
    }

    static async removeVariant(variantId) {
        return http.delete(`/features/variants/${variantId}`);
    }

    // ============================================================================
    // 4. CUSTOMIZATION MAPPING
    // ============================================================================

    static async createCustomization(recipeId, data) {
        return http.post(`/features/recipes/${recipeId}/customizations`, data);
    }

    static async getRecipeCustomizations(recipeId, activeOnly = true) {
        return http.get(`/features/recipes/${recipeId}/customizations?activeOnly=${activeOnly}`);
    }

    static async applyCustomizationsToIngredients(recipeId, customizationIds) {
        return http.post(`/features/recipes/${recipeId}/customizations/apply`, { customizationIds });
    }

    static async getCustomizationCategories() {
        return http.get('/features/customization-categories');
    }

    static async getAdjustmentTypes() {
        return http.get('/features/adjustment-types');
    }

    static async updateCustomization(customizationId, data) {
        return http.put(`/features/customizations/${customizationId}`, data);
    }

    static async deleteCustomization(customizationId) {
        return http.delete(`/features/customizations/${customizationId}`);
    }

    static async createCommonCustomizations(recipeId, spicyIngredientIds) {
        return http.post(`/features/recipes/${recipeId}/customizations/common`, { spicyIngredientIds });
    }

    // ============================================================================
    // 5. CHANGE LOGGING
    // ============================================================================

    // (Change-log methods removed in SP6/SP7 — the backend /features/changes routes are
    // retired; the canonical mapping audit trail is ItemMappingLog.)

}

export default FeaturesService;